import type {
  LatestWorkflowForEntityResponse,
  WorkflowEvent,
} from '@/shared/hooks/api/workflows'

/** Step ids from server `standardApplicabilityDiscoveryWorkflow` */
export type DiscoveryStepId =
  | 'get_applicability_context'
  | 'discover_integrated_components'
  | 'retrieve_type_c_standards'
  | 'llm_applicability'
  | 'resolve_mandatory_iso_12100'
  | 'persist_applicability'

export type DiscoveryTaskStatus =
  | 'pending'
  | 'active'
  | 'done'
  | 'skipped'

export type LlmTrackPhase = 'idle' | 'running' | 'done'

export type LlmTrackState =
  | { phase: 'idle' }
  | {
      phase: 'running'
      batchIndex: number
      batchTotal: number
    }
  | { phase: 'done' }

/** Batch progress for Type C applicability LLM (1-based `index` / `total`). */
export function standardApplicabilityLlmProgressPercent(
  track: LlmTrackState
): number | null {
  if (track.phase === 'done') {
    return 100
  }
  if (track.phase === 'running') {
    if (track.batchTotal <= 0) {
      return null
    }
    return Math.min(
      100,
      Math.round((track.batchIndex / track.batchTotal) * 100)
    )
  }
  return null
}

export type StandardApplicabilityDiscoveryTaskState = {
  steps: Record<DiscoveryStepId, DiscoveryTaskStatus>
  llmTypeC: LlmTrackState
  /** From `step.completed` on retrieve_type_c_standards (`result.candidatesCount`). */
  retrieveTypeCCandidateCount: number | null
}

const WORKFLOW_TYPE = 'standard_applicability_discovery'

export const DISCOVERY_ORDERED_STEPS: DiscoveryStepId[] = [
  'get_applicability_context',
  'discover_integrated_components',
  'retrieve_type_c_standards',
  'llm_applicability',
  'resolve_mandatory_iso_12100',
  'persist_applicability',
]

function emptySteps(): Record<DiscoveryStepId, DiscoveryTaskStatus> {
  return {
    get_applicability_context: 'pending',
    discover_integrated_components: 'pending',
    retrieve_type_c_standards: 'pending',
    llm_applicability: 'pending',
    resolve_mandatory_iso_12100: 'pending',
    persist_applicability: 'pending',
  }
}

export function getInitialStandardApplicabilityDiscoveryTaskState(): StandardApplicabilityDiscoveryTaskState {
  return {
    steps: emptySteps(),
    llmTypeC: { phase: 'idle' },
    retrieveTypeCCandidateCount: null,
  }
}

function cloneState(
  state: StandardApplicabilityDiscoveryTaskState
): StandardApplicabilityDiscoveryTaskState {
  return {
    steps: { ...state.steps },
    llmTypeC: state.llmTypeC,
    retrieveTypeCCandidateCount: state.retrieveTypeCCandidateCount,
  }
}

function indexOfStep(stepId: DiscoveryStepId): number {
  return DISCOVERY_ORDERED_STEPS.indexOf(stepId)
}

/** Mark earlier pending steps as done/skipped when later steps start. */
function applyStepStarted(
  next: StandardApplicabilityDiscoveryTaskState,
  stepId: DiscoveryStepId
): void {
  const targetIndex = indexOfStep(stepId)
  for (let index = 0; index < targetIndex; index++) {
    const id = DISCOVERY_ORDERED_STEPS[index]!
    const status = next.steps[id]
    if (status === 'pending') {
      if (id === 'discover_integrated_components') {
        next.steps[id] = 'skipped'
      } else {
        next.steps[id] = 'done'
      }
    }
  }

  if (stepId === 'retrieve_type_c_standards' && next.steps.discover_integrated_components === 'pending') {
    next.steps.discover_integrated_components = 'skipped'
  }

  next.steps[stepId] = 'active'
}

function applyLlmStreamEvent(
  next: StandardApplicabilityDiscoveryTaskState,
  _stream: 'type_c',
  kind: 'started' | 'completed',
  batch?: { index: number; total: number }
): void {
  const key = 'llmTypeC'
  if (kind === 'started' && batch != null) {
    next[key] = {
      phase: 'running',
      batchIndex: batch.index,
      batchTotal: batch.total,
    }
    return
  }
  if (kind === 'completed' && batch != null) {
    if (batch.index === batch.total) {
      next[key] = { phase: 'done' }
    } else {
      next[key] = {
        phase: 'running',
        batchIndex: batch.index,
        batchTotal: batch.total,
      }
    }
  }
}

/**
 * Fold workflow poll/SSE events into checklist state for standard applicability discovery.
 */
export function reduceStandardApplicabilityDiscoveryTaskState(
  state: StandardApplicabilityDiscoveryTaskState,
  event: WorkflowEvent
): StandardApplicabilityDiscoveryTaskState {
  const next = cloneState(state)

  if (event.type === 'workflow.started') {
    const workflowType =
      'workflowType' in event &&
      typeof (event as { workflowType?: string }).workflowType === 'string'
        ? (event as { workflowType: string }).workflowType
        : undefined
    if (workflowType === WORKFLOW_TYPE) {
      return getInitialStandardApplicabilityDiscoveryTaskState()
    }
    return state
  }

  if (event.type === 'workflow.completed') {
    for (const id of DISCOVERY_ORDERED_STEPS) {
      next.steps[id] = 'done'
    }
    if (next.llmTypeC.phase === 'running') {
      next.llmTypeC = { phase: 'done' }
    }
    return next
  }

  if (event.type === 'step.started') {
    const step = (event as { step?: string }).step
    if (
      step != null &&
      DISCOVERY_ORDERED_STEPS.includes(step as DiscoveryStepId)
    ) {
      applyStepStarted(next, step as DiscoveryStepId)
    }
    return next
  }

  if (event.type === 'step.completed') {
    const step = (event as { step?: string }).step
    const result = (event as { result?: unknown }).result
    if (
      step != null &&
      DISCOVERY_ORDERED_STEPS.includes(step as DiscoveryStepId)
    ) {
      if (
        result !== undefined &&
        result !== null &&
        typeof result === 'object' &&
        'candidatesCount' in result
      ) {
        const rawCount = (result as { candidatesCount: unknown }).candidatesCount
        const count =
          typeof rawCount === 'number' ? rawCount : Number(rawCount)
        if (!Number.isNaN(count)) {
          if (step === 'retrieve_type_c_standards') {
            next.retrieveTypeCCandidateCount = count
          }
        }
      }
      next.steps[step as DiscoveryStepId] = 'done'
      if (step === 'llm_applicability') {
        if (next.llmTypeC.phase === 'running') {
          next.llmTypeC = { phase: 'done' }
        }
      }
    }
    return next
  }

  if (event.type === 'llm.started') {
    const step = (event as { step?: string }).step
    const stepId = (event as { stepId?: string }).stepId
    const batch = (event as { batch?: { index: number; total: number } }).batch
    if (step === 'llm_applicability' && stepId === 'type_c' && batch != null) {
      applyLlmStreamEvent(next, stepId, 'started', batch)
    }
    return next
  }

  if (event.type === 'llm.completed') {
    const step = (event as { step?: string }).step
    const stepId = (event as { stepId?: string }).stepId
    const batch = (event as { batch?: { index: number; total: number } }).batch
    if (step === 'llm_applicability' && stepId === 'type_c' && batch != null) {
      applyLlmStreamEvent(next, stepId, 'completed', batch)
    }
    return next
  }

  return state
}

/** Fold persisted workflow events into checklist state (no animation). */
export function buildStandardApplicabilityDiscoveryTaskStateFromEvents(
  events: WorkflowEvent[]
): StandardApplicabilityDiscoveryTaskState {
  return events.reduce(
    reduceStandardApplicabilityDiscoveryTaskState,
    getInitialStandardApplicabilityDiscoveryTaskState()
  )
}

export type StandardApplicabilityDiscoveryRecovery = {
  isStandardsWorkflowRunning: boolean
  standardsDiscoveryCompleted: boolean
  standardsWorkflowError: string | null
  standardsDiscoveryTaskState: StandardApplicabilityDiscoveryTaskState
}

/** Derive onboarding UI state from a latest-workflow poll response. */
export function deriveStandardApplicabilityDiscoveryRecovery(
  workflow: LatestWorkflowForEntityResponse | undefined,
  fallbackErrorMessage: string
): StandardApplicabilityDiscoveryRecovery | null {
  if (workflow == null || workflow.status === 'none') {
    return null
  }

  const standardsDiscoveryTaskState =
    buildStandardApplicabilityDiscoveryTaskStateFromEvents(workflow.events)

  if (workflow.status === 'running') {
    return {
      isStandardsWorkflowRunning: true,
      standardsDiscoveryCompleted: false,
      standardsWorkflowError: null,
      standardsDiscoveryTaskState,
    }
  }

  if (workflow.status === 'completed') {
    return {
      isStandardsWorkflowRunning: false,
      standardsDiscoveryCompleted: true,
      standardsWorkflowError: null,
      standardsDiscoveryTaskState,
    }
  }

  if (workflow.status === 'failed' || workflow.status === 'cancelled') {
    return {
      isStandardsWorkflowRunning: false,
      standardsDiscoveryCompleted: false,
      standardsWorkflowError: workflow.error?.trim() || fallbackErrorMessage,
      standardsDiscoveryTaskState,
    }
  }

  return null
}
